import React, { useEffect, useRef, useState } from 'react';
import {
  LockClosedIcon,
  QrCodeIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { authService } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import { orgBrandingService } from '../../services/orgBranding';
import BoclawLogo from '../icons/BoclawLogo';
import {
  GlassCard,
  GlassField,
  LoginSpinner,
  WeChatIcon,
} from './LoginControls';
import {
  emitLoginEvent,
  formatLoginPhone,
  readLoginMethod,
  safeLoginRedirect,
  type LoginMethod,
} from './loginPolicy';
import { useLoginForm, type LoginMode } from './useLoginForm';
import { useWeChatLogin } from './useWeChatLogin';
import { PhoneRegionSelect } from './PhoneRegionSelect';
import { AppQrLogin } from './AppQrLogin';
import { LightBackground, LoginIllustration } from './LegacyAuthBackground';
import { LoginMethodPicker } from './LoginMethodPicker';
import { DEFAULT_LOGIN_METHODS } from '../../utils/loginMethodCatalog';
import './LoginPage.css';

interface LoginPageProps {
  onToast?: (message: string) => void;
}

/** 玻璃拟态登录入口；只组合视图，认证、短信和扫码策略由专用服务及 hooks 管理。 */
const LoginPage: React.FC<LoginPageProps> = ({ onToast }) => {
  const [mode, setMode] = useState<LoginMode>(() =>
    readLoginMethod(window.location.search),
  );
  const [appQrBusy, setAppQrBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [qrImageFailed, setQrImageFailed] = useState(false);
  const [notice, setNotice] = useState('');
  const [branding, setBranding] = useState(orgBrandingService.getBranding());
  const [capabilities, setCapabilities] = useState<{
    phone: boolean;
    wechat: boolean;
    appqr: boolean;
    methods?: LoginMethod[];
  } | null>(null);
  const [, setLanguage] = useState(i18nService.getLanguage());
  const agreementTimer = useRef<ReturnType<typeof setTimeout>>();
  const noticeTimer = useRef<ReturnType<typeof setTimeout>>();
  const view = useRef<HTMLDivElement>(null);
  const redirect = useRef(safeLoginRedirect(window.location.search));
  const t = (key: string) => i18nService.t(key);

  /** 协议不默认勾选；提交、发短信和创建扫码会话共用同一个门禁。 */
  const requireAgreement = () => {
    if (agreed) return true;
    setAgreementError(true);
    clearTimeout(agreementTimer.current);
    agreementTimer.current = setTimeout(() => setAgreementError(false), 2000);
    return false;
  };
  /** 短信通知留在当前登录页，避免向全局 Toast 泄漏失效流程的消息。 */
  const showNotice = (message: string) => {
    setNotice(message);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 1600);
  };
  /** 四种认证步骤共用成功出口，跳转目标已经过同源校验。 */
  const onSuccess = () => {
    onToast?.(t('authLoginSuccess'));
    if (redirect.current) window.location.replace(redirect.current);
    else {
      const url = new URL(window.location.href);
      url.searchParams.delete('method');
      url.searchParams.delete('redirect');
      window.history.replaceState(window.history.state, '', url);
    }
  };
  const form = useLoginForm({
    mode,
    agreed,
    phoneEnabled: capabilities?.phone === true || mode === 'wechat_phone_bind',
    requireAgreement,
    onMode: setMode,
    onSuccess,
    onNotice: showNotice,
  });
  const qr = useWeChatLogin({
    enabled: mode === 'wechat' && agreed && capabilities?.wechat === true,
    onAuthenticated: onSuccess,
    onBindPhone: form.beginBinding,
  });
  const busy = appQrBusy || form.submitting || form.sending;
  useEffect(() => setQrImageFailed(false), [qr.url]);

  useEffect(() => {
    let cancelled = false;
    let loading = false;
    /** 入口能力随前台恢复更新，串行请求防止乱序；不刷新页面或清空表单。 */
    const refreshCapabilities = async () => {
      if (cancelled || loading || document.visibilityState === 'hidden') return;
      loading = true;
      try {
        const flags = await authService.getLoginCapabilities();
        if (!cancelled) setCapabilities(flags);
      } catch {
        if (!cancelled) setCapabilities(previous => previous
          ? { ...previous, appqr: false }
          : { phone: false, wechat: false, appqr: false });
      } finally { loading = false; }
    };
    void refreshCapabilities();
    const interval = window.setInterval(() => void refreshCapabilities(), 30_000);
    window.addEventListener('focus', refreshCapabilities);
    document.addEventListener('visibilitychange', refreshCapabilities);
    const unbrand = orgBrandingService.subscribe(() =>
      setBranding(orgBrandingService.getBranding()),
    );
    const unlanguage = i18nService.subscribe(() =>
      setLanguage(i18nService.getLanguage()),
    );
    emitLoginEvent('login_view', {
      entry: redirect.current ? 'redirect' : 'direct',
    });
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshCapabilities);
      document.removeEventListener('visibilitychange', refreshCapabilities);
      unbrand();
      unlanguage();
      clearTimeout(agreementTimer.current);
      clearTimeout(noticeTimer.current);
    };
  }, []);
  useEffect(() => {
    // 区号也使用按钮，仍优先聚焦号码或账号输入，避免打开页面就聚焦菜单。
    const firstControl =
      view.current?.querySelector<HTMLElement>('input') ??
      view.current?.querySelector<HTMLElement>('button');
    firstControl?.focus({ preventScroll: true });
    if (mode === '2fa' || mode === 'wechat_phone_bind') return;
    const url = new URL(window.location.href);
    url.searchParams.set('method', mode);
    window.history.replaceState(window.history.state, '', url);
  }, [mode]);

  useEffect(() => {
    // 未获得明确能力前不创建二维码；已开始兑换交由服务端裁决，不能打断成功回执。
    if (mode === 'appqr' && capabilities && !capabilities.appqr && !appQrBusy) {
      setMode('password');
      setNotice(i18nService.t('authAppQrDisabled'));
    }
  }, [mode, capabilities, appQrBusy]);

  /** 403 只结束扫码流程，不清除当前会话或其他登录表单。 */
  const handleAppQrDisabled = () => {
    setCapabilities(previous => ({ phone: false, wechat: false, ...previous, appqr: false }));
    setMode('password');
    setNotice(t('authAppQrDisabled'));
  };

  /** 一步切换登录方式，同时销毁密码、验证码和未完成的二次认证上下文。 */
  const switchMethod = (next: LoginMethod) => {
    if (busy || (next === 'appqr' && !capabilities?.appqr)) return;
    emitLoginEvent('login_method_switch', { from: mode, to: next });
    form.reset();
    setNotice('');
    setShowPassword(false);
    setAgreementError(false);
    setMode(next);
  };
  const methodLabel = (method: LoginMethod) =>
    method === 'appqr' ? t('authAppQrTab') : t(
      method === 'phone'
        ? 'authLoginTabPhone'
        : method === 'password'
          ? 'loginPasswordMethod'
          : 'loginWeChatMethod',
    );
  const platformUrl = authService.getBaseUrl();
  const legalLink = (path: string) => {
    try {
      const url = new URL(`${platformUrl.replace(/\/+$/, '')}/${path}`);
      return /^https?:$/.test(url.protocol) ? url.href : undefined;
    } catch {
      return undefined;
    }
  };
  const phoneMode = mode === 'phone' || mode === 'wechat_phone_bind';
  const phoneUnavailable = mode === 'phone' && capabilities?.phone !== true;
  const requestingCode = phoneMode && !form.codeRequested;
  const smsLabel = form.sending
    ? t('loginSending')
    : form.cooldown.seconds > 0
      ? t('loginCountdown').replace('{seconds}', String(form.cooldown.seconds))
      : t(form.codeRequested ? 'loginResend' : 'authSendSmsCode');
  const primaryLabel = requestingCode ? smsLabel : t('login');

  // 发送成功后才挂载验证码输入框，焦点随步骤推进，不要求用户再次点击。
  useEffect(() => {
    if (phoneMode && form.codeRequested) {
      view.current
        ?.querySelector<HTMLInputElement>('#login-code')
        ?.focus({ preventScroll: true });
    }
  }, [phoneMode, form.codeRequested]);
  const unavailableMessage = !capabilities
    ? t('loginLoadingMethods')
    : mode === 'wechat'
      ? t('loginWeChatUnavailable')
      : t('loginPhoneUnavailable');

  return (
    <main className="bomate-login">
      <div className="login-background" aria-hidden="true"><LightBackground showIllustration={false} forceLight /></div>
      <div className="login-layout">
        <LoginIllustration />
        <GlassCard>
          <header className="login-brand">
            <span className="login-brand-logo">
              <BoclawLogo
                size={52}
                orgLogoUrl={branding?.logoUrl}
                orgCode={branding?.code}
                orgName={branding?.name}
              />
            </span>
            <h1 id="login-brand">{branding?.code || 'BoMate'}</h1>
            <p>{branding?.name || t('loginAssistant')}</p>
          </header>
          <div className="login-view" key={mode} ref={view}>
            <form
              className="login-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (mode === 'appqr') return;
                if (requestingCode) void form.sendSms();
                else void form.submit();
              }}
              noValidate
              aria-label={
                mode === '2fa'
                  ? t('authTwoFactorTitle')
                  : mode === 'wechat_phone_bind'
                    ? t('authWeChatBindPhoneTitle')
                    : methodLabel(mode)
              }
            >
              {mode === 'appqr' && capabilities === null && <div className="login-qr-view" role="status">{t('loginLoadingMethods')}</div>}
              {mode === 'appqr' && (capabilities?.appqr || appQrBusy) && (
                agreed ? (
                  <AppQrLogin onDisabled={handleAppQrDisabled} onBack={() => switchMethod('password')} onSuccess={onSuccess} onBusyChange={setAppQrBusy} />
                ) : (
                  // 协议撤销时卸载扫码组件，复用其清理逻辑停止轮询并撤销旧票据。
                  <div className="login-qr-view">
                    <h2 className="login-qr-title"><DevicePhoneMobileIcon />{t('authAppQrTab')}</h2>
                    <div className="login-qr-frame">
                      <div className="login-qr">
                        <div className="login-qr-overlay">
                          <button type="button" onClick={requireAgreement}>{t('loginConsentQr')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
              {mode === 'password' && (
                <>
                  <GlassField
                    id="login-account"
                    name="username"
                    aria-label={t('loginAccount')}
                    placeholder={t('authUsernamePlaceholder')}
                    autoComplete="username"
                    value={form.account}
                    disabled={busy}
                    error={form.errors.account}
                    onChange={(event) => {
                      form.setAccount(event.target.value);
                      form.clearError('account');
                    }}
                  />
                  <GlassField
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    aria-label={t('loginPassword')}
                    placeholder={t('enterPassword')}
                    autoComplete="current-password"
                    value={form.password}
                    disabled={busy}
                    error={form.errors.password}
                    onChange={(event) => {
                      form.setPassword(event.target.value);
                      form.clearError('password');
                    }}
                    trailing={
                      <button
                        type="button"
                        className="login-eye"
                        aria-label={t(
                          showPassword
                            ? 'loginHidePassword'
                            : 'loginShowPassword',
                        )}
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((value) => !value)}
                      >
                        {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                      </button>
                    }
                  />
                  {form.captcha && (
                    <GlassField
                      id="login-captcha"
                      aria-label={t('authVerificationCode')}
                      placeholder={t('authVerificationCode')}
                      autoComplete="off"
                      value={form.captchaCode}
                      disabled={busy}
                      error={form.errors.captcha}
                      onChange={(event) => {
                        form.setCaptchaCode(event.target.value);
                        form.clearError('captcha');
                      }}
                      trailing={
                        <button
                          type="button"
                          className="login-captcha"
                          aria-label={t('authVerificationCode')}
                          onClick={() => void form.loadCaptcha()}
                        >
                          <img
                            src={form.captcha.image}
                            alt={t('authVerificationCode')}
                          />
                        </button>
                      }
                    />
                  )}
                  <a
                    className="login-forgot"
                    href={legalLink('reset')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('loginForgot')}
                  </a>
                </>
              )}
              {phoneMode && (
                <>
                  {mode === 'wechat_phone_bind' && (
                    <>
                      <h2 className="login-subtitle">
                        {t('authWeChatBindPhoneTitle')}
                      </h2>
                      <p className="login-hint">
                        {t('authWeChatBindPhoneHint')}
                      </p>
                    </>
                  )}
                  <GlassField
                    id="login-phone"
                    name="tel"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    aria-label={t('authPhone')}
                    placeholder={t('authPhonePlaceholder')}
                    value={formatLoginPhone(
                      mode === 'phone' ? form.phone : form.bindPhone,
                      form.phoneRegion,
                    )}
                    disabled={busy}
                    leading={
                      <PhoneRegionSelect
                        value={form.phoneRegion}
                        disabled={busy}
                        onChange={form.changePhoneRegion}
                      />
                    }
                    error={form.errors.phone}
                    onChange={(event) => {
                      form.updatePhone(event.target.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void form.sendSms();
                      }
                    }}
                  />
                  {form.codeRequested && (
                    <GlassField
                      id="login-code"
                      name="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      aria-label={t('loginSmsLabel')}
                      placeholder={t('authVerificationCodePlaceholder')}
                      value={form.code}
                      disabled={busy}
                      error={form.errors.code}
                      onChange={(event) => {
                        form.setCode(
                          event.target.value.replace(/\D/g, '').slice(0, 6),
                        );
                        form.clearError('code');
                      }}
                      trailing={
                        <button
                          type="button"
                          className="login-text-button"
                          onClick={() => void form.sendSms()}
                          disabled={
                            busy ||
                            form.cooldown.seconds > 0 ||
                            phoneUnavailable
                          }
                        >
                          {smsLabel}
                        </button>
                      }
                    />
                  )}
                  {phoneUnavailable && (
                    <p className="login-hint" role="status">
                      {unavailableMessage}
                    </p>
                  )}
                </>
              )}
              {mode === '2fa' && (
                <>
                  <h2 className="login-subtitle">{t('authTwoFactorTitle')}</h2>
                  <GlassField
                    id="login-2fa"
                    autoComplete="one-time-code"
                    aria-label={t('authTwoFactorCode')}
                    placeholder={t('authTwoFactorCodePlaceholder')}
                    value={form.code}
                    disabled={busy}
                    error={form.errors.code}
                    onChange={(event) => {
                      form.setCode(event.target.value);
                      form.clearError('code');
                    }}
                  />
                </>
              )}
              {mode === 'wechat' && (
                <div className="login-qr-view">
                  <h2 className="login-qr-title">
                    <WeChatIcon />
                    {t('loginWeChatMethod')}
                  </h2>
                  <div className="login-qr-frame">
                    <div
                      className={`login-qr${qr.expired || qrImageFailed ? ' is-expired' : ''}`}
                      aria-live="polite"
                      aria-busy={qr.loading}
                    >
                      {qr.url && (
                        <img
                          className="login-qr-image"
                          src={qr.url}
                          alt={t('loginWeChatMethod')}
                          referrerPolicy="no-referrer"
                          onError={() => setQrImageFailed(true)}
                        />
                      )}
                      {qr.url && !qr.expired && !qrImageFailed && (
                        <span className="login-qr-scan" />
                      )}
                      {(!qr.url || qr.expired || qrImageFailed) && (
                        <div className="login-qr-overlay">
                          {capabilities?.wechat !== true ? (
                            <p>{unavailableMessage}</p>
                          ) : !agreed ? (
                            <button type="button" onClick={requireAgreement}>
                              {t('loginConsentQr')}
                            </button>
                          ) : qr.loading ? (
                            <>
                              <LoginSpinner />
                              <p>{t('authWeChatQrLoading')}</p>
                            </>
                          ) : (
                            <>
                              <p>
                                {qr.expired
                                  ? t('authWeChatQrExpired')
                                  : t('connectionFailed')}
                              </p>
                              <button
                                type="button"
                                className="login-text-button"
                                onClick={qr.refresh}
                              >
                                {t('loginRetryQr')}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="login-qr-caption">
                    {t('loginScan')}
                    <br />
                    {t('loginScanRegister')}
                  </p>
                  {qr.url && !qr.expired && !qrImageFailed && (
                    <button
                      type="button"
                      className="login-text-button"
                      onClick={qr.refresh}
                    >
                      {t('loginRetryQr')}
                    </button>
                  )}
                  {qr.error && (
                    <p className="login-error" role="status">
                      {qr.error}
                    </p>
                  )}
                </div>
              )}
              {form.error && (
                <p className="login-error" role="status">
                  {form.error}
                </p>
              )}
              {mode !== 'wechat' && mode !== 'appqr' && (
                <button
                  type="submit"
                  className="login-primary"
                  disabled={
                    busy ||
                    phoneUnavailable ||
                    (requestingCode && form.cooldown.seconds > 0)
                  }
                  aria-label={primaryLabel}
                  aria-busy={busy}
                  aria-describedby={
                    requestingCode && form.errors.code
                      ? 'login-send-error'
                      : undefined
                  }
                >
                  {busy ? <LoginSpinner /> : primaryLabel}
                </button>
              )}
              {requestingCode && form.errors.code && (
                <p className="login-error" id="login-send-error" role="alert">
                  {form.errors.code}
                </p>
              )}
            </form>
          </div>
          <div
            className={`login-agreement${agreementError ? ' is-invalid' : ''}`}
          >
            {agreementError && (
              <div className="login-agreement-bubble" role="alert">
                {t('authPleaseAgree')}
              </div>
            )}
            <input
              id="login-agreement"
              type="checkbox"
              checked={agreed}
              disabled={busy}
              onChange={(event) => {
                setAgreed(event.target.checked);
                setAgreementError(false);
              }}
              aria-describedby={
                agreementError ? 'login-agreement-text' : undefined
              }
            />
            <span id="login-agreement-text">
              <label htmlFor="login-agreement">{t('authAgreePrefix')}</label>
              <a
                href={legalLink('user-agreement')}
                target="_blank"
                rel="noopener noreferrer"
              >
                《{t('authUserAgreement')}》
              </a>
              <a
                href={legalLink('privacy-policy')}
                target="_blank"
                rel="noopener noreferrer"
              >
                《{t('authPrivacyPolicy')}》
              </a>
            </span>
          </div>
          <LoginMethodPicker
            key={`methods-${mode}`}
            label={t('loginOtherMethods')}
            disabled={busy}
            items={(capabilities?.methods ?? DEFAULT_LOGIN_METHODS)
              .filter((method) => (method !== 'appqr' || capabilities?.appqr === true) && method !== mode && (mode !== 'wechat_phone_bind' || method !== 'phone'))
              .map((method) => ({
                id: method,
                label: method === 'appqr' ? t('authAppQrTab') : t(method === 'phone' ? 'loginSwitchPhone' : method === 'wechat' ? 'loginSwitchWechat' : 'loginSwitchPassword'),
                ariaLabel: methodLabel(method),
                icon: method === 'appqr' ? <QrCodeIcon aria-hidden="true" />
                  : method === 'wechat' ? <WeChatIcon />
                    : method === 'phone' ? <DevicePhoneMobileIcon aria-hidden="true" /> : <LockClosedIcon aria-hidden="true" />,
              }))}
            onSelect={(id) => switchMethod(id as LoginMethod)}
          />
          <p className="login-copyright">
            Copyright © {new Date().getFullYear()} BoMate. All rights reserved.
          </p>
        </GlassCard>
      </div>
      {notice && (
        <div className="login-toast" role="status">
          {notice}
        </div>
      )}
    </main>
  );
};
export default LoginPage;
